var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { filterChinese, isEmail } from "../core/password.js";
import { createPasswordRules } from "../ui/password-rules.js";
import { isMobile } from "../utils.js";
import { ClearIcon, VisibilityIcon } from "./icon.js";
export const createInputChangeHandler = (onChange, transformValue) => (event) => onChange(transformValue ? transformValue(event.target.value) : event.target.value);
export const createClearInputHandler = (onChange) => () => onChange("");
export const createPasswordVisibilityHandler = (setPasswordVisible) => () => setPasswordVisible((visible) => !visible);
const TAB_STOP_SELECTOR = "[data-smm-auth-tab-stop='true']:not(:disabled)";
export const focusSiblingTabStop = (element, direction) => {
    const scope = element.closest(".actionWrapper") || element.closest("#smm-auth-widget-root");
    if (!scope)
        return false;
    const tabStops = Array.from(scope.querySelectorAll(TAB_STOP_SELECTOR)).filter((element) => element.tabIndex >= 0 && element.offsetParent !== null);
    const currentIndex = tabStops.indexOf(element);
    const nextElement = currentIndex >= 0 ? tabStops[currentIndex + direction] : undefined;
    if (!nextElement)
        return false;
    nextElement.focus();
    return true;
};
export const createInputKeyDownHandler = (onKeyDown) => (event) => {
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    if (event.defaultPrevented || event.key !== "Tab")
        return;
    if (focusSiblingTabStop(event.currentTarget, event.shiftKey ? -1 : 1)) {
        event.preventDefault();
    }
};
export const getInputPresentation = (type, passwordVisible) => ({
    inputType: type === "password" && !passwordVisible ? "password" : "text",
    visibilityLabel: type === "password" ? (passwordVisible ? "Hide password" : "Show password") : undefined,
});
export const Input = (_a) => {
    var { type = "text", value, onChange, transformValue, className, onKeyDown } = _a, inputProps = __rest(_a, ["type", "value", "onChange", "transformValue", "className", "onKeyDown"]);
    const [passwordVisible, setPasswordVisible] = React.useState(false);
    const isPassword = type === "password";
    const presentation = getInputPresentation(type, passwordVisible);
    return (_jsxs("span", { className: "smm-auth-input-shell", children: [_jsx("input", Object.assign({}, inputProps, { className: ["smm-auth-input", className].filter(Boolean).join(" "), type: presentation.inputType, value: value, "data-smm-auth-tab-stop": "true", onChange: createInputChangeHandler(onChange, transformValue), onKeyDown: createInputKeyDownHandler(onKeyDown) })), value ? (_jsx("button", { type: "button", "aria-label": "Clear input", className: "smm-auth-input-action", style: { marginLeft: 8 }, onClick: createClearInputHandler(onChange), children: _jsx(ClearIcon, {}) })) : null, isPassword ? (_jsx("span", { role: "img", "aria-label": "eye-invisible", className: "smm-auth-input-action", style: { fontSize: 16 }, onClick: createPasswordVisibilityHandler(setPasswordVisible), children: _jsx(VisibilityIcon, { visible: passwordVisible }) })) : null] }));
};
const findFirstInput = (element) => {
    const props = element.props;
    if (element.type === "input" || typeof element.type !== "string") {
        return {
            found: true,
            id: typeof props.id === "string" ? props.id : undefined,
            value: props.value,
        };
    }
    for (const child of React.Children.toArray(props.children)) {
        if (React.isValidElement(child)) {
            const result = findFirstInput(child);
            if (result.found)
                return result;
        }
    }
    return { found: false };
};
const attachFieldInput = (element, inputId, name, errorId, invalid, onValueChange) => {
    if (element.type === "input" || typeof element.type !== "string") {
        const props = element.props;
        return {
            element: React.cloneElement(element, {
                id: inputId,
                name,
                "aria-invalid": invalid || undefined,
                "aria-describedby": invalid ? errorId : undefined,
                onChange: (...args) => {
                    var _a, _b;
                    (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, ...args);
                    const firstArgument = args[0];
                    const value = element.type === "input" &&
                        typeof firstArgument === "object" &&
                        firstArgument !== null &&
                        "target" in firstArgument
                        ? (_b = firstArgument.target) === null || _b === void 0 ? void 0 : _b.value
                        : firstArgument;
                    onValueChange(value);
                },
            }),
            attached: true,
        };
    }
    const props = element.props;
    let attached = false;
    const children = React.Children.map(props.children, (child) => {
        if (attached || !React.isValidElement(child))
            return child;
        const result = attachFieldInput(child, inputId, name, errorId, invalid, onValueChange);
        attached = result.attached;
        return result.element;
    });
    return {
        element: React.cloneElement(element, undefined, children),
        attached,
    };
};
export const validateFieldValue = (value, rules = []) => {
    var _a;
    for (const rule of rules) {
        if (rule.required && (value == null || String(value).trim() === "")) {
            return rule.message || "This field is required";
        }
        const stringValue = value == null ? "" : String(value).trim();
        if (rule.type === "email" && stringValue && !isEmail(stringValue)) {
            return rule.message || "Please enter a valid email";
        }
        const validationError = (_a = rule.validator) === null || _a === void 0 ? void 0 : _a.call(rule, value);
        if (validationError) {
            return validationError;
        }
    }
    return "";
};
export const Field = React.forwardRef(function Field({ label, name, rules = [], extra, children, style, className, error, onValidationChange }, ref) {
    var _a;
    const generatedId = React.useId();
    const errorId = `${generatedId}-error`;
    const input = findFirstInput(children);
    const inputId = (_a = input.id) !== null && _a !== void 0 ? _a : generatedId;
    const [internalError, setInternalError] = React.useState("");
    const currentValueRef = React.useRef(input.value);
    currentValueRef.current = input.value;
    const validate = React.useCallback((value) => {
        const nextError = validateFieldValue(value, rules);
        setInternalError(nextError);
        onValidationChange === null || onValidationChange === void 0 ? void 0 : onValidationChange(nextError);
        return nextError === "";
    }, [onValidationChange, rules]);
    React.useImperativeHandle(ref, () => ({
        validate: () => validate(currentValueRef.current),
    }));
    const displayedError = error !== null && error !== void 0 ? error : internalError;
    const fieldClassName = ["smm-auth-field", displayedError && "smm-auth-field-error", className]
        .filter(Boolean)
        .join(" ");
    const fieldInput = attachFieldInput(children, inputId, name, errorId, Boolean(displayedError), (value) => {
        currentValueRef.current = value;
        validate(value);
    }).element;
    return (_jsxs("div", { className: fieldClassName, style: style, children: [_jsxs("label", { htmlFor: inputId, children: [_jsx("span", { className: "smm-auth-label", children: label }), extra] }), fieldInput, displayedError ? (_jsxs("div", { id: errorId, className: "smm-auth-field-error-message", role: "alert", children: [_jsx("i", {}), displayedError] })) : null] }));
});
export const PasswordInput = ({ id, value, onChange, }) => (_jsx("input", { id: id, className: "smm-auth-input", type: "password", value: value, onChange: (event) => onChange(filterChinese(event.target.value)), onPaste: (event) => {
        event.preventDefault();
        onChange(filterChinese(event.clipboardData.getData("text")));
    } }));
export const PasswordRulesPopover = ({ children, id, name, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, onChange, t, }) => {
    const [open, setOpen] = React.useState(false);
    const [placement, setPlacement] = React.useState("right");
    const containerRef = React.useRef(null);
    const popoverRef = React.useRef(null);
    const isH5 = isMobile();
    const rules = createPasswordRules(t);
    const value = children.props.value;
    React.useLayoutEffect(() => {
        if (!open)
            return;
        const updatePlacement = () => {
            const container = containerRef.current;
            const popover = popoverRef.current;
            if (!container || !popover)
                return;
            setPlacement(container.getBoundingClientRect().right + 12 + popover.offsetWidth > window.innerWidth ? "left" : "right");
        };
        updatePlacement();
        window.addEventListener("resize", updatePlacement);
        return () => window.removeEventListener("resize", updatePlacement);
    }, [open]);
    const input = React.cloneElement(children, {
        id,
        name,
        "aria-invalid": ariaInvalid,
        "aria-describedby": ariaDescribedBy,
        onChange: (nextValue) => {
            children.props.onChange(nextValue);
            onChange === null || onChange === void 0 ? void 0 : onChange(nextValue);
        },
        onFocus: (event) => {
            var _a, _b;
            (_b = (_a = children.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            if (!isH5)
                setOpen(true);
        },
        onBlur: (event) => {
            var _a, _b;
            (_b = (_a = children.props).onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            setOpen(false);
        },
    });
    if (isH5) {
        return input;
    }
    return (_jsxs("div", { ref: containerRef, className: "smm-auth-password-rules-popover", children: [input, open ? (_jsx("ul", { ref: popoverRef, className: `smm-auth-password-rules-list smm-auth-password-rules-list-${placement}`, role: "status", children: rules.map((rule) => {
                    const passed = Boolean(value) && rule.check(value);
                    return (_jsxs("li", { className: passed ? "pass" : "fail", children: [_jsx("span", { className: "smm-auth-password-rule-icon", "aria-hidden": "true", children: passed ? "✓" : "✕" }), _jsx("span", { children: rule.label })] }, rule.key));
                }) })) : null] }));
};
export const EmailInput = ({ id, value, onChange, }) => (_jsx("input", { id: id, className: "smm-auth-input", value: value, autoFocus: true, onChange: (event) => onChange(event.target.value.trim()) }));
export const SubmitButton = ({ style, children, loading, disabled, onClick, }) => (_jsx("button", { className: `smm-auth-submit${loading ? " smm-auth-submit-loading" : ""}`, style: style, type: "button", "aria-busy": loading, "data-smm-auth-tab-stop": "true", disabled: loading || disabled, onClick: onClick, children: children }));
//# sourceMappingURL=components.js.map